import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import marketing from '../assets/images/marketing.png';
import ai from '../assets/images/agent.png';
import web from '../assets/images/web.png';
import StatsBar from './StatsBar';

const float = keyframes`
  0% {
    transform: translate(0, 0) rotate(0deg);
  }
  50% {
    transform: translate(10px, 15px) rotate(5deg);
  }
  100% {
    transform: translate(0, 0) rotate(0deg);
  }
`;

const floatReverse = keyframes`
  0% {
    transform: translate(0, 0) rotate(0deg);
  }
  50% {
    transform: translate(-10px, -15px) rotate(-5deg);
  }
  100% {
    transform: translate(0, 0) rotate(0deg);
  }
`;

const BannerContainer = styled.div`
  text-align: center;
  position: relative;
  overflow: hidden;
  padding: 2rem 1rem;
  
  @media (min-width: 768px) {
    padding: 6rem 2rem;
  }
`;

const MainHeading = styled.h1`
  font-size: 2.5rem;
  padding: 0 1rem;
  margin-bottom: 1rem;
  
  @media (min-width: 768px) {
    font-size: 4rem;
    margin-bottom: 1.5rem;
  }
`;

const SubHeading = styled.p`
  font-size: 1rem;
  padding: 0 1rem;
  margin-bottom: 2rem;
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
    margin-bottom: 2.5rem;
  }
`;

const DisabledCTAButton = styled(Link)`
  background: rgba(99, 102, 241, 0.5);
  color: rgba(255, 255, 255, 0.7);
  padding: 1rem 2rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  margin: 2rem 0;
  cursor: not-allowed;
  pointer-events: none;
  position: relative;
  display: inline-block;
`;

const ComingSoonText = styled.span`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: -1.5rem;
  font-size: 0.8rem;
  color: #6366f1;
  white-space: nowrap;
  opacity: 0.8;
`;

const ServicesGrid = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  max-width: 100%;
  padding: 0 1rem;
  margin: 2rem auto;
  
  @media (min-width: 968px) {
    flex-direction: row;
    gap: 0;
    max-width: 1200px;
    margin: 0 auto;
  }
`;

const ServiceCard = styled.div`
  width: 100%;
  max-width: 350px;
  min-height: 200px;
  margin: 0;
  transform: rotate(0deg) !important;
  padding: 1.5rem;
 border-radius: 20px;

  @media (min-width: 968px) {
    width: 400px;
    min-height: 250px;
    padding: 2rem;
    margin-top: ${props => props.marginTop || '0'};
    transform: rotate(${props => props.rotate || '0deg'}) !important;
  }

  background: ${props => props.bgColor || '#e8f3ff'};
  background-image: url(${props => props.bgImage});
  background-size: contain;
  background-position: center;
//   background-blend-mode: overlay;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.bgColor || '#e8f3ff'};
    opacity: 0.85;
    z-index: 1;
  }

  /* Add grayscale filter to background image */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url(${props => props.bgImage});
    background-size: cover;
    background-position: center;
    filter: grayscale(100%);
    opacity: 0.2;
    z-index: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px) rotate(0deg) !important;
    
    @media (min-width: 968px) {
      transform: translateY(-5px) rotate(${props => props.rotate || '0deg'}) !important;
    }
  }
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #000;
  position: relative;
  z-index: 2;
`;

const ServiceDescription = styled.p`
  color: #4a4a4a;
  line-height: 1.6;
  position: relative;
  z-index: 2;
`;

// Decorative shapes
const Shape = styled.div`
  display: none;
  
  @media (min-width: 968px) {
    display: block;
    position: absolute;
    border-radius: 50%;
    z-index: 0;
    animation: ${float} 6s ease-in-out infinite;
  }
`;

const OrangeShape = styled(Shape)`
  width: 150px;
  height: 150px;
  background: #F4A460;
  top: 10%;
  right: 5%;
`;

const BlueShape = styled(Shape)`
  width: 100px;
  height: 100px;
  background: #B0E0E6;
  bottom: 20%;
  left: 10%;
`;

const PurpleShape = styled(Shape)`
  width: 120px;
  height: 120px;
  background: #DDA0DD;
  top: 30%;
  left: 15%;
`;

// Additional shape components
const Triangle = styled.div`
  display: none;
  
  @media (min-width: 968px) {
    display: block;
    position: absolute;
    width: 0;
    height: 0;
    border-left: 50px solid transparent;
    border-right: 50px solid transparent;
    border-radius: 10px;
    border-bottom: 80px solid ${props => props.color};
    transform: rotate(${props => props.rotate || '0deg'});
    opacity: 0.6;
    z-index: 0;
    animation: ${float} 8s ease-in-out infinite;
    transition: transform 0.3s ease-out;
  }
`;

const Square = styled.div`
  display: none;
  
  @media (min-width: 968px) {
    display: block;
    position: absolute;
    width: 60px;
    height: 60px;
    background: ${props => props.color};
    transform: rotate(${props => props.rotate || '0deg'});
    opacity: 0.6;
    z-index: 0;
    animation: ${float} 7s ease-in-out infinite;
    transition: transform 0.3s ease-out;
  }
`;

const Pentagon = styled.div`
  display: none;
  
  @media (min-width: 968px) {
    display: block;
    position: absolute;
    width: 40px;
    height: 40px;
    background: ${props => props.color};
    clip-path: polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%);
    opacity: 0.6;
    z-index: 0;
    animation: ${float} 9s ease-in-out infinite;
    transition: transform 0.3s ease-out;
  }
`;

const HomeBanner = () => {
  const [mousePosition, setMousePosition] = React.useState({ x: 0, y: 0 });
  
  const handleMouseMove = (e) => {
    const { clientX, clientY } = e;
    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
    
    // Calculate relative mouse position
    const x = (clientX - left) / width - 0.5;
    const y = (clientY - top) / height - 0.5;
    
    setMousePosition({ x, y });
  };

  const getTransform = (factor = 1) => {
    const { x, y } = mousePosition;
    return `translate(${x * 30 * factor}px, ${y * 30 * factor}px)`;
  };

  return (
    <BannerContainer onMouseMove={handleMouseMove}>
      <OrangeShape style={{ transform: getTransform(0.5) }} />
      <BlueShape style={{ transform: getTransform(0.8) }} />
      <PurpleShape style={{ transform: getTransform(1.2) }} />
      
      <Triangle 
        color="#F4A460" 
        style={{ 
          top: '15%', 
          right: '15%',
          transform: `${getTransform(0.7)} rotate(25deg)`
        }}
      />
      
      <Square 
        color="#B0E0E6" 
        style={{ 
          bottom: '25%', 
          borderRadius: '10px',
          right: '10%',
          transform: `${getTransform(1)} rotate(45deg)`
        }}
      />
      
      <Triangle 
        color="#DDA0DD" 
        style={{ 
          top: '40%', 
          left: '5%',
          transform: `${getTransform(0.9)} rotate(-15deg)`
        }}
      />
      
      <Square 
        color="#F4A460" 
        style={{ 
          top: '20%', 
          left: '20%',
          borderRadius: '10px',
          transform: `${getTransform(1.1)} rotate(15deg)`
        }}
      />
      
      <Pentagon 
        color="#B0E0E6" 
        style={{ 
          bottom: '15%', 
          right: '25%',
          transform: getTransform(0.6)
        }}
      />
      
      <MainHeading>The all-in-one technical solution tailored for everyone</MainHeading>
      <SubHeading>
      Whether you’re a professional, a creative, or a business owner, we deliver not just functionality, but also efficiency, automation, scalability, and security. <br /> <br />No more red pill, blue pill - pain of choosing the right technical solution provider.</SubHeading>
      
      <DisabledCTAButton to="/start-free-trial">
        Get AI Quote as reference
        <ComingSoonText>coming soon</ComingSoonText>
      </DisabledCTAButton>

      <ServicesGrid>
        <ServiceCard 
          bgColor="#B0E0E6" 
          bgImage={ai}
          rotate="-8deg"
          marginTop="100px"
        >
          <ServiceTitle>AI Agent ›</ServiceTitle>
          <ServiceDescription>
            Sell anything. Upload your content and leave
            the hosting, checkout, and delivery to us.
          </ServiceDescription>
        </ServiceCard>

        <ServiceCard 
          bgColor="#F4A460"
          bgImage={web}
          marginTop="0px"
        >
          <ServiceTitle>Web Application ›</ServiceTitle>
          <ServiceDescription>
            Build a beautiful website in less than 10 minutes
            with professionally designed templates.
          </ServiceDescription>
        </ServiceCard>

        <ServiceCard 
          bgColor="#DDA0DD"
          bgImage={marketing}
          rotate="8deg"
          marginTop="100px"
        >
          <ServiceTitle>Marketing ›</ServiceTitle>
          <ServiceDescription>
            Beautiful email marketing that works perfectly
            with your online store and website.
          </ServiceDescription>
        </ServiceCard>
      </ServicesGrid>
      <StatsBar />
    </BannerContainer>
  );
};

export default HomeBanner;